/**
 * services/auth/auth.ts — SK Hospitality platform-auth business logic.
 * All calls go through the shared client (api/axiosInstance) using the platform
 * URLs from api/endpoints — same pattern as SK Catalog's services/auth.
 *
 * SK Hospitality owns NO auth UI: the platform (skplatform-api) issues the
 * httpOnly cookie session. We only probe it (/v1/me), sign out, and finish a
 * magic-link login (which sets the cookies server-side).
 */
import { apiClient } from '../../api/axiosInstance';
import { Platform_Me_Url, Platform_Logout_Url, Platform_Magic_Verify_Url } from '../../api/endpoints';
import type { AuthResult } from '../../schemas/auth/auth';

export type { AuthResult } from '../../schemas/auth/auth';

/**
 * Probe the platform session cookie. Returns an AuthResult (sentinel token
 * 'session') when a valid cookie session exists. Adapter calls ride the same
 * cookie via withCredentials — no JWT is stored for the session to work.
 *
 * Deliberately does NOT catch: a failure here (401, 5xx, or a transport error)
 * propagates to the caller as a rejected promise. The boot gate (AuthGate) is
 * the one place that decides what a failure means — see the comment there on
 * why swallowing every error into "not authenticated" caused an infinite
 * app<->platform redirect loop for brand-new signups (matches SK Catalog's
 * rootNavigator.tsx, which hit and fixed the same class of bug).
 */
export async function checkSession(): Promise<AuthResult> {
  const { data } = await apiClient.get<{ data: { user: any; org: any; memberships: any[] } }>(Platform_Me_Url);
  const d = data.data;
  const fullName = [d.user?.first_name, d.user?.last_name].filter(Boolean).join(' ');
  return {
    token: 'session',
    role: 'operator',
    profileId: d.user?.id ?? d.user?.user_id ?? '',
    email: d.user?.email ?? '',
    firstName: d.user?.first_name ?? '',
    displayName: fullName,
    // org identity is platform-owned; read it here so orgName/orgId survive login
    // (the sentinel 'session' token has no org_name claim to decode).
    orgId: d.org?.id ?? '',
    orgName: d.org?.name ?? '',
    emailVerified: d.user?.email_verified ?? false,
    profileComplete: d.user?.profile_complete ?? false,
  };
}

/**
 * Sign out via the platform: POST /v1/auth/logout clears the shared httpOnly
 * cookies server-side (they can't be cleared from JS). Best-effort.
 */
export async function signOut(): Promise<void> {
  try {
    await apiClient.post(Platform_Logout_Url);
  } catch {
    /* already logged out / session expired — nothing to clear */
  }
}

/**
 * Finish a one-click magic-link login. The platform's POST /v1/auth/magic-verify
 * sets the httpOnly session cookies and returns { data: { message } } (no token
 * in the body), so we complete by re-probing the now-valid session.
 */
export async function verifyMagicLink(token: string): Promise<AuthResult> {
  await apiClient.post(Platform_Magic_Verify_Url, { token });
  return checkSession();
}
